import Signal from '@webgl/core/Signal'
import { GLContext } from 'nanogl/types'

import { captureMessage } from '@/instrumentation'

/**
 * Limit frame dt
 * @param dt
 * @returns
 */
function clampDt(dt: number): number {
  if (dt > 1 / 5 || dt < 1 / 180) {
    dt = 1 / 60
  }
  return dt
}

function now() {
  return performance.now()
}

class GLView {
  pixelRatio: number
  gl: GLContext

  width: number
  height: number
  canvasWidth: number
  canvasHeight: number
  previousTime: number

  private _rafId: number
  private _playing: boolean
  private _contextLost = false
  private _resizeObs: ResizeObserver

  private _deferedResize = false
  private _deferedWidth = 0
  private _deferedHeight = 0

  onRender = new Signal<number>()
  onResize = new Signal<void>()

  /**
   *
   * @param cvs
   * @param opts
   */
  constructor(
    readonly canvas: HTMLCanvasElement,
    { depth = true, alpha = false, pixelRatio = -1 } = {}
  ) {
    if (pixelRatio < 0) {
      this.pixelRatio = Math.min(2.0, window.devicePixelRatio)
    } else {
      this.pixelRatio = pixelRatio
    }

    const opts: WebGLContextAttributes = {
      depth: depth,
      antialias: true,
      stencil: true,
      alpha: alpha,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    }

    const gl = (canvas.getContext('webgl2', opts) ||
      canvas.getContext('webgl', opts) ||
      canvas.getContext('experimental-webgl', opts) ||
      canvas.getContext('webgl')) as GLContext | null

    if (!gl) {
      captureMessage('WebGL unavailable: failed to acquire a rendering context', 'fatal')
      throw new Error('WebGL is not available in this environment')
    }

    this.gl = gl

    this.gl.clearColor(1.0, 0.96, 0.9, 1.0)
    this.gl.clear(this.gl.COLOR_BUFFER_BIT)

    this.width = 0
    this.height = 0

    this.canvasWidth = 0
    this.canvasHeight = 0

    this.previousTime = now()
    this._rafId = 0
    this._playing = false

    // Listen from construction rather than from start(): shaders compile and uniforms are grabbed
    // during loading, and a loss there would otherwise go unreported.
    this.canvas.addEventListener('webglcontextlost', this._onContextLost, false)
    this.canvas.addEventListener('webglcontextrestored', this._onContextRestored, false)

    this._resizeObs = new ResizeObserver((entries) => {
      // const { width, height } = entries[0].contentRect;
      // this._handleResize(width, height);
      const { width, height } = entries[0].contentRect
      this._deferedResize = true
      this._deferedWidth = width
      this._deferedHeight = height
    })
  }

  visibilityChange = () => {
    if (!document.hidden) {
      // Reset previousTime when the page becomes visible
      this.previousTime = performance.now()
    }
  }

  // Calling preventDefault() keeps the context restorable instead of letting the
  // browser kill it permanently (which would leave a silent black canvas).
  private _onContextLost = (e: Event) => {
    e.preventDefault()
    this._contextLost = true
    this._playing = false
    window.cancelAnimationFrame(this._rafId)
    this._rafId = 0
    captureMessage('WebGL context lost', 'error')
  }

  // Telemetry only by design: GPU resources are destroyed on context loss and the engine can't
  // re-upload them mid-session, so we measure occurrence rather than attempt an unsafe restart.
  private _onContextRestored = () => {
    captureMessage('WebGL context restored', 'warning')
  }

  updateSize(): void {
    this.canvas.width = Math.ceil((this.pixelRatio * this.canvasWidth) / 4.0) * 4.0
    this.canvas.height = Math.ceil((this.pixelRatio * this.canvasHeight) / 4.0) * 4.0
    this.width = this.gl.drawingBufferWidth
    this.height = this.gl.drawingBufferHeight
    this.onResize.emit()
  }

  _handleResize(w: number, h: number): boolean {
    if (isNaN(w) || isNaN(h) || w === 0 || h === 0) {
      return false
    }
    if (w !== this.canvasWidth || h !== this.canvasHeight) {
      this.canvasWidth = w
      this.canvasHeight = h
      this.updateSize()
    }
    return true
  }

  start(): void {
    // A lost context can't be rendered to; keep the canvas static instead of throwing every frame.
    // This stays true after a restore: nothing recreates the textures, buffers and programs, which
    // all die with the old context, so rendering would read dead GL objects.
    if (this._contextLost || this.gl.isContextLost()) return
    this._handleResize(this.canvas.clientWidth, this.canvas.clientHeight)
    this._resizeObs.observe(this.canvas)
    this._playing = true
    this.previousTime = now()
    this.frame(now())
    document.addEventListener('visibilitychange', this.visibilityChange)
  }

  stop(): void {
    this._resizeObs.disconnect()
    this._playing = false
    this._rafId = 0
    document.removeEventListener('visibilitychange', this.visibilityChange)
  }

  _requestFrame(): void {
    window.cancelAnimationFrame(this._rafId)
    this._rafId = window.requestAnimationFrame(this.frame)
  }

  frame = (time: number): void => {
    if (this._deferedResize) {
      this._deferedResize = false
      this._handleResize(this._deferedWidth, this._deferedHeight)
    }

    if (!this._playing) {
      return
    }

    let dt = (time - this.previousTime) / 1000
    this.previousTime = time

    if (document.hidden || dt < 0.0) {
      // If the page is hidden, set dt to 0
      dt = 0
    } else {
      // Clamp dt so a long main-thread stall doesn't produce a huge simulation jump.
      dt = clampDt(dt)
    }

    this.onRender.emit(dt)

    if (this._playing) {
      this._requestFrame()
    }
  }
}

export default GLView
